import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Coins, Mic, Square, Calendar, Clock, Stethoscope,
  Activity, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VoiceAgentDetail = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, token, updateCredits } = useAuth();

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [transcript, setTranscript] = useState([]);

  // Use refs to track mutable state that the stop handler needs
  const vapiInstanceRef = useRef(null);
  const isCallActiveRef = useRef(false);
  const transcriptContainerRef = useRef(null);
  const deductCreditsOnEndRef = useRef(null);

  useEffect(() => {
    fetchAgent();
  }, [agentId]);

  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [transcript]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (vapiInstanceRef.current && isCallActiveRef.current) {
        try { vapiInstanceRef.current.stop(); } catch (e) { /* ignore */ }
      }
    };
  }, []);

  const fetchAgent = async () => {
    try {
      const response = await axios.get(`${API}/voice-agents/${agentId}`);
      setAgent(response.data);
    } catch (error) {
      console.error('Failed to fetch voice agent:', error);
      toast.error('Voice agent not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const fetchCredentials = async () => {
    try {
      const response = await axios.get(
        `${API}/voice-agents/${agentId}/credentials`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to fetch voice agent credentials';
      toast.error(detail);
      return null;
    }
  };

  const validateCredits = async () => {
    try {
      await axios.post(
        `${API}/voice-agents/${agentId}/start-call`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail || 'Insufficient credits';
      toast.error(detail);
      return false;
    }
  };

  const deductCreditsOnEnd = async (transcriptMessages) => {
    try {
      const response = await axios.post(
        `${API}/voice-agents/${agentId}/end-call`,
        { transcript: transcriptMessages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (updateCredits) {
        updateCredits(response.data.remaining_credits);
      }
      const { query_count, credits_used } = response.data;
      if (credits_used > 0) {
        toast.success(`Call ended: ${query_count} queries — ${credits_used} credits deducted`);
      } else {
        toast.info('Call ended with no queries — no credits deducted');
      }
    } catch (error) {
      console.error('Failed to deduct credits on call end:', error);
    }
  };

  // Keep ref updated so the Vapi event handler always uses the latest version
  useEffect(() => {
    deductCreditsOnEndRef.current = deductCreditsOnEnd;
  });

  const initVapi = useCallback((key) => {
    if (!window.Vapi) {
      console.warn('Vapi SDK not loaded yet');
      return null;
    }

    // If we already have one, destroy it first
    if (vapiInstanceRef.current) {
      try { vapiInstanceRef.current.stop(); } catch (e) { /* ignore */ }
      vapiInstanceRef.current = null;
    }

    try {
      const instance = new window.Vapi(key);

      instance.on('call-start', () => {
        isCallActiveRef.current = true;
        setIsCallActive(true);
        setIsConnected(true);
        setIsStarting(false);
        setTranscript(prev => [...prev, { type: 'system', text: 'Secure channel established with Elara.' }]);
      });

      instance.on('call-end', () => {
        isCallActiveRef.current = false;
        setIsCallActive(false);
        setIsConnected(false);
        setIsSpeaking(false);
        setIsStarting(false);
        setTranscript(prev => {
          const updated = [...prev, { type: 'system', text: 'Call ended.' }];
          // Send transcript to backend for credit deduction based on user queries
          const transcriptForBackend = updated.filter(m => m.type === 'user' || m.type === 'assistant');
          if (deductCreditsOnEndRef.current) deductCreditsOnEndRef.current(transcriptForBackend);
          return updated;
        });
      });

      instance.on('speech-start', () => setIsSpeaking(true));
      instance.on('speech-end', () => setIsSpeaking(false));

      instance.on('message', (msg) => {
        if (msg.type === 'transcript' && msg.transcriptType === 'final') {
          if (msg.transcript) {
            setTranscript(prev => [...prev, { type: msg.role, text: msg.transcript }]);
          }
        }
      });

      instance.on('error', (e) => {
        console.error('Vapi Error:', e);
        isCallActiveRef.current = false;
        setIsCallActive(false);
        setIsConnected(false);
        setIsStarting(false);
        setTranscript(prev => {
          const updated = [...prev, { type: 'system', text: 'Connection error. Please try again.' }];
          // Deduct credits for any queries made before the error
          const transcriptForBackend = updated.filter(m => m.type === 'user' || m.type === 'assistant');
          if (transcriptForBackend.length > 0 && deductCreditsOnEndRef.current) {
            deductCreditsOnEndRef.current(transcriptForBackend);
          }
          return updated;
        });
      });

      vapiInstanceRef.current = instance;
      return instance;
    } catch (e) {
      console.error('Vapi init error:', e);
      return null;
    }
  }, []);

  const handleStopCall = useCallback(() => {
    if (vapiInstanceRef.current) {
      try {
        vapiInstanceRef.current.stop();
      } catch (e) {
        console.error('Error stopping call:', e);
      }
      // Force state update immediately even if event doesn't fire
      isCallActiveRef.current = false;
      setIsCallActive(false);
      setIsConnected(false);
      setIsSpeaking(false);
    }
  }, []);

  const toggleCall = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to use voice agents');
      navigate('/login');
      return;
    }

    // STOP the call
    if (isCallActiveRef.current || isCallActive) {
      handleStopCall();
      return;
    }

    // START the call
    setIsStarting(true);

    // 1. Fetch credentials from backend
    const creds = await fetchCredentials();
    if (!creds) {
      setIsStarting(false);
      return;
    }

    // 2. Validate user has enough credits
    const hasCredits = await validateCredits();
    if (!hasCredits) {
      setIsStarting(false);
      return;
    }

    // 3. Init Vapi & start (credits deducted on call end based on transcript)
    const instance = initVapi(creds.vapi_public_key);
    if (!instance) {
      toast.error('Voice SDK not loaded yet. Please wait a moment and try again.');
      setIsStarting(false);
      return;
    }

    try {
      await instance.start(creds.vapi_assistant_id);
    } catch (e) {
      console.error('Failed to start call:', e);
      toast.error('Failed to start voice call');
      setIsStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#F87217] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Navigation - same as other agent pages */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-bold text-lg">Marketplace</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/marketplace" className="text-slate-600 hover:text-slate-900 transition-colors">
              Marketplace
            </Link>
            <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition-colors">
              Pricing
            </Link>
          </div>

          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-sm">
                  <Coins className="w-4 h-4 text-[#F87217]" />
                  <span className="text-sm font-medium text-slate-900">{user?.credits || 0}</span>
                </div>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm"
                >
                  Dashboard
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Back Button + Agent Info */}
      <div className="max-w-7xl mx-auto w-full px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/marketplace')}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#F87217]/10 rounded-sm flex items-center justify-center">
            <Mic className="w-5 h-5 text-[#F87217]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{agent?.name}</h2>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700 border-0 rounded-sm text-xs">
                {agent?.tier}
              </Badge>
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Coins className="w-3 h-3" /> {agent?.credits_per_query} credits/query
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Console */}
      <section className="flex-1 px-6 pb-6 max-w-[1600px] mx-auto w-full">
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-lg flex" style={{ height: '72vh' }}>

          {/* Sidebar - Capabilities */}
          <aside className="w-72 border-r border-slate-200 hidden md:flex flex-col p-5">
            <div className="mb-5">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">System Status</h3>
              <div className={`flex items-center gap-3 p-3 rounded-xl border ${isConnected ? 'bg-green-50 border-green-200' : 'bg-teal-50 border-teal-100'}`}>
                <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 shadow-[0_0_10px_#10B981]' : 'bg-red-500'}`} />
                <span className={`text-sm font-bold ${isConnected ? 'text-green-800' : 'text-teal-800'}`}>
                  {isConnected ? 'Live Secure Connection' : 'Ready to Connect'}
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Capabilities</h3>
              <div className="space-y-3">
                <div className="p-3 rounded-xl border border-slate-100 bg-white shadow-sm">
                  <div className="flex items-center gap-3 mb-1.5">
                    <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg"><Calendar className="w-4 h-4" /></div>
                    <span className="font-bold text-sm text-slate-700">Booking</span>
                  </div>
                  <p className="text-xs text-slate-500">"Book an appointment with Dr. Paul for next Tuesday."</p>
                </div>
                <div className="p-3 rounded-xl border border-slate-100 bg-white shadow-sm">
                  <div className="flex items-center gap-3 mb-1.5">
                    <div className="p-1.5 bg-orange-50 text-orange-600 rounded-lg"><Clock className="w-4 h-4" /></div>
                    <span className="font-bold text-sm text-slate-700">Rescheduling</span>
                  </div>
                  <p className="text-xs text-slate-500">"Move my appointment to 4 PM instead."</p>
                </div>
                <div className="p-3 rounded-xl border border-slate-100 bg-white shadow-sm">
                  <div className="flex items-center gap-3 mb-1.5">
                    <div className="p-1.5 bg-purple-50 text-purple-600 rounded-lg"><Stethoscope className="w-4 h-4" /></div>
                    <span className="font-bold text-sm text-slate-700">Specialist Search</span>
                  </div>
                  <p className="text-xs text-slate-500">"I need a heart doctor in Delhi."</p>
                </div>
              </div>
            </div>
          </aside>

          {/* Chat Panel */}
          <main className="flex-1 flex flex-col bg-slate-50">
            <div className="p-4 border-b border-slate-200 bg-white/50 backdrop-blur-sm flex justify-between items-center">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Live Transcript</span>
              <span className="px-2 py-1 rounded bg-slate-200 text-slate-600 text-[10px] font-bold">SECURE CHANNEL</span>
            </div>

            <div ref={transcriptContainerRef} className="flex-1 overflow-y-auto p-6 scroll-smooth">
              {transcript.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center opacity-40">
                  <Activity className="w-16 h-16 text-teal-300 mb-4" />
                  <p className="text-sm font-medium text-slate-400">Waiting for voice input...</p>
                </div>
              )}

              {transcript.map((msg, i) => {
                if (msg.type === 'system') {
                  return (
                    <div key={i} className="text-center text-xs text-slate-400 font-medium my-4 italic">
                      {msg.text}
                    </div>
                  );
                }
                if (msg.type === 'assistant') {
                  return (
                    <div key={i} className="bg-white border border-slate-200 rounded-xl rounded-bl-none p-4 text-slate-700 shadow-sm text-sm mb-3 max-w-[85%] animate-in fade-in">
                      <div className="text-[10px] font-bold uppercase mb-1 opacity-50">Elara (AI)</div>
                      {msg.text}
                    </div>
                  );
                }
                return (
                  <div key={i} className="bg-teal-700 text-white rounded-xl rounded-br-none p-4 text-sm mb-3 ml-auto max-w-[85%] shadow-md animate-in fade-in">
                    <div className="text-[10px] font-bold uppercase mb-1 opacity-50">Patient</div>
                    {msg.text}
                  </div>
                );
              })}
            </div>
          </main>

          {/* Visualizer Panel */}
          <aside className="w-72 border-l border-slate-200 flex flex-col items-center justify-center p-8 bg-white relative">
            <div className="absolute top-6 right-6">
              <div className="w-2 h-2 bg-slate-300 rounded-full animate-pulse" />
            </div>

            <div className="text-center mb-8">
              <h2 className="text-lg font-bold text-slate-900">Elara</h2>
              <p className="text-xs text-slate-400 font-medium uppercase tracking-widest mt-1">AI Health Associate</p>
            </div>

            <button
              onClick={toggleCall}
              disabled={isStarting}
              className={`w-20 h-20 rounded-full flex items-center justify-center text-white shadow-lg transition-all cursor-pointer mb-6 ${
                isStarting
                  ? 'bg-amber-500 shadow-amber-300'
                  : isCallActive
                    ? 'bg-red-600 shadow-red-300 hover:bg-red-700'
                    : 'bg-teal-700 shadow-teal-300 hover:scale-105'
              } ${isSpeaking && isCallActive ? 'scale-110' : ''}`}
            >
              {isStarting ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : isCallActive ? (
                <Square className="w-8 h-8" />
              ) : (
                <Mic className="w-8 h-8" />
              )}
            </button>

            <p className="text-sm font-bold text-slate-400">
              {isStarting ? 'Connecting...' : isCallActive ? 'Connected — Tap to End' : 'Tap to Connect'}
            </p>

            {/* Doctors List */}
            <div className="mt-8 w-full border-t border-slate-100 pt-4">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Specialists</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Dr. John</span><span className="text-xs text-teal-600">Cardiology</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Dr. Anthony</span><span className="text-xs text-indigo-600">Neurology</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Dr. Ravi</span><span className="text-xs text-teal-600">Cardiology</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Dr. Vikram</span><span className="text-xs text-indigo-600">Neurology</span></div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Vapi SDK loader */}
      <VapiLoader />
    </div>
  );
};

/**
 * Loads the Vapi SDK from ESM and sets window.Vapi
 */
const VapiLoader = () => {
  useEffect(() => {
    if (window.Vapi) return;

    const script = document.createElement('script');
    script.type = 'module';
    script.textContent = `
      import Vapi from 'https://esm.sh/@vapi-ai/web';
      window.Vapi = Vapi.default || Vapi;
      window.dispatchEvent(new Event('vapi-ready'));
    `;
    document.head.appendChild(script);
  }, []);

  return null;
};

export default VoiceAgentDetail;
